// Imperative documents-context fold.
//
// The documents package is deferred out of the eager shell bundle — its page
// bodies, fetch blocks and the Crepe editor only matter on /documents, so the
// routes fold it via lazySharedLibPage. But the /files desktop now opens a .md
// in that same editor (2026-08-08 per Nika: "md files can be opened in notes
// editor (in modal)"), and it does so from a route that never folds documents.
//
// Same shape as ensureChatContext, and the same reason for it: one add-missing
// fold, single-flight, retryable, latched on the state root. Nothing here
// mounts anything — the caller decides where the editor goes, because /files
// puts it in the viewer window rather than on a page.
// Bare shared-library import, not a deep pages/lazyPage.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0). Deferred-call
// wrapper, not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellFoldSharedLibContext = (...args) => _wsShared.pages.foldSharedLibContext(...args)

let _documentsFoldInFlight = null

// Fold the full documents context into the live registry. Returns true when the
// context is registered, false when the fold failed — callers must bail quietly
// on false rather than throwing, so a broken chunk degrades to "the editor
// didn't open", not a crashed desktop.
export const ensureDocumentsContext = async function () {
  // TWO roots, deliberately distinct: foldSharedLibContext registers into the
  // ELEMENT root's `.context`, while the latch lives on the STATE root. Passing
  // the state root registers NOTHING and reports success — the trap chatFold
  // documents from having been caught live.
  const rootEl = typeof this?.getRoot === 'function' ? this.getRoot() : this
  const stateRoot = this?.state?.root || rootEl?.state?.root || rootEl?.state || null
  if (!rootEl || !stateRoot) return false
  if (stateRoot._documentsCtxFolded) return true
  if (!_documentsFoldInFlight) {
    _documentsFoldInFlight = import('@symbo-ls/workspace-documents')
      .then((mod) => {
        const lib = mod?.default || mod
        if (!lib || (!lib.components && !lib.functions)) {
          throw new Error('documents context resolved empty — stale/mismatched bundle')
        }
        shellFoldSharedLibContext(rootEl, lib)
        // Latches ONCE per app lifetime: the registry keeps folded components
        // across tenant switches, so this key is deliberately NOT in the
        // workspace-scoped wipe registry.
        stateRoot.update({ _documentsCtxFolded: true }, { preventFetch: true })
        return true
      })
      .catch((err) => {
        _documentsFoldInFlight = null
        console.error('[documents] context fold failed:', err?.message || err)
        return false
      })
  }
  return _documentsFoldInFlight
}
